// UX-014: detail pages link back to their list with `?focus=<id>` so the
// operator lands on the row they came from. Nothing read that parameter, so
// every one of those links quietly dropped the operator at the top of an
// unfiltered list.
//
// This is the read side. `focusId` is the requested row; `focusProps(id)`
// gives a row the scroll-into-view behaviour and a temporary highlight ring.
//
// Deliberately not a filter: focus points at a row inside the full list rather
// than narrowing it, so the operator keeps their bearings. Filtering that
// changes what is listed belongs in the URL as its own parameter and must be
// visible in the UI (see ActivityLogPage).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface FocusRowProps {
  ref?: (node: HTMLElement | null) => void;
  className: string;
  /** Set on the focused row so tests and assistive tech can find it. */
  'data-focused'?: 'true';
}

export function useFocusParam(paramName = 'focus'): {
  focusId: string | null;
  focusProps: (id: string) => FocusRowProps;
} {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get(paramName)?.trim() || null;

  // The highlight fades once the operator has had a chance to see it. The id
  // itself stays in the URL so a refresh still lands in the same place.
  const [highlighted, setHighlighted] = useState<string | null>(requested);
  const scrolledRef = useRef<string | null>(null);

  useEffect(() => {
    setHighlighted(requested);
    scrolledRef.current = null;
    if (!requested) return undefined;
    const timer = window.setTimeout(() => setHighlighted(null), 2400);
    return () => window.clearTimeout(timer);
  }, [requested]);

  const focusProps = useCallback(
    (id: string): FocusRowProps => {
      if (id !== requested) return { className: '' };
      return {
        ref: (node: HTMLElement | null) => {
          // Scroll once per requested id — re-renders must not yank the
          // viewport back while the operator is reading.
          if (!node || scrolledRef.current === requested) return;
          scrolledRef.current = requested;
          node.scrollIntoView({ block: 'center', behavior: 'smooth' });
        },
        className:
          highlighted === id
            ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface transition-shadow motion-reduce:transition-none'
            : '',
        'data-focused': 'true',
      };
    },
    [requested, highlighted],
  );

  return { focusId: requested, focusProps };
}
